import React, { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom";
import Dropdown from 'react-bootstrap/Dropdown';
import userAvatar from "../assets/img/img1.jpg";
import { getFullName, getPosName, getUser, logout } from "../services/authorize";
import { useAuth } from "../services/authProvider";
import { useLanguage } from "../contexts/LanguageContext";
import {
  executiveMenu, teachingMenu, advisorMenu, manageTeachingMenu, registrationMenu,
  assessmentMenu, headOfGeneralAcademicMenu, libraryMenu, dormitoryMenu,
  disciplineMenu, bankMenu
} from "../data/Menu";
import NotificationBell from "../issueReporting/components/NotificationBell";

const MENU_CATALOG = [
  ...executiveMenu.map(m => ({ ...m, requiredPositions: [90, 99] })),
  ...teachingMenu.map(m => ({ ...m, requiredPositions: [1, 5, 99] })),
  ...advisorMenu.map(m => ({ ...m, requiredPositions: [2, 99] })),
  ...manageTeachingMenu.map(m => ({ ...m, requiredPositions: [3, 4, 5, 17, 99] })),
  ...headOfGeneralAcademicMenu.map(m => ({ ...m, requiredPositions: [3, 4, 17, 99] })),
  ...registrationMenu.map(m => ({ ...m, requiredPositions: [8, 9, 99] })),
  ...assessmentMenu.map(m => ({ ...m, requiredPositions: [10, 11, 99] })),
  ...libraryMenu.map(m => ({ ...m, requiredPositions: [13, 99] })),
  ...dormitoryMenu.map(m => ({ ...m, requiredPositions: [17, 99] })),
  ...disciplineMenu.map(m => ({ ...m, requiredPositions: [16, 99] })),
  ...bankMenu.map(m => ({ ...m, requiredPositions: [18, 99] })),
];

const UPLOAD_BASE = (process.env.REACT_APP_API || "http://localhost:3002/api")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");

const resolveUploadUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${UPLOAD_BASE}${path.startsWith("/") ? path : `/${path}`}`;
};

export default function Header({ onSkin }) {
  const navigate = useNavigate();
  const { posIds } = useAuth();
  const { pathname } = useLocation();
  const avatarSrc = resolveUploadUrl(getUser()?.t_photo) || userAvatar;
  const isIslamicRoute = pathname.startsWith('/islamic');
  const { lang, setLang } = useLanguage();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const searchRef = useRef(null);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return MENU_CATALOG
      .filter(item =>
        item.label.toLowerCase().includes(q) &&
        item.requiredPositions.some(p => posIds?.includes(p))
      )
      .slice(0, 8);
  }, [query, posIds]);

  useEffect(() => {
    setActiveIdx(0);
  }, [results]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    navigate(item.link);
    setQuery('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && results[activeIdx]) handleSelect(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <Link
      to=""
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="dropdown-link"
    >
      {children}
    </Link>
  ));

  const toggleSidebar = (e) => {
    e.preventDefault();
    const body = document.body;

    if (body.classList.contains("sidebar-offset")) {
      body.classList.toggle("sidebar-show");
      return;
    }

    if (window.matchMedia("(max-width: 991px)").matches) {
      body.classList.toggle("sidebar-show");
      return;
    }

    // Desktop: toggle class and update CSS variable — no DOM querying needed
    const willHide = !body.classList.contains("sidebar-hide");
    body.classList.toggle("sidebar-hide");

    const isRTL  = document.documentElement.classList.contains("islamic-rtl");
    const varName = isRTL ? "--sidebar-rtl-offset" : "--sidebar-offset";

    if (willHide) {
      document.documentElement.style.setProperty(varName, "0px");
    } else {
      document.documentElement.style.removeProperty(varName);
    }
  }

  const skinMode = (e) => {
    e.preventDefault();
    e.target.classList.add("active");
    let node = e.target.parentNode.firstChild;
    while (node) {
      if (node !== e.target && node.nodeType === Node.ELEMENT_NODE)
        node.classList.remove("active");
      node = node.nextElementSibling || node.nextSibling;
    }
    let skin = e.target.textContent.toLowerCase();
    let HTMLTag = document.querySelector("html");
    if (skin === "dark") {
      HTMLTag.setAttribute("data-skin", skin);
      localStorage.setItem('skin-mode', skin);
      onSkin && onSkin(skin);
    } else {
      HTMLTag.removeAttribute("data-skin");
      localStorage.removeItem('skin-mode');
      onSkin && onSkin('');
    }
  };

  const sidebarSkin = (e) => {
    e.preventDefault();
    e.target.classList.add("active");
    let node = e.target.parentNode.firstChild;
    while (node) {
      if (node !== e.target && node.nodeType === Node.ELEMENT_NODE)
        node.classList.remove("active");
      node = node.nextElementSibling || node.nextSibling;
    }
    let skin = e.target.textContent.toLowerCase();
    let HTMLTag = document.querySelector("html");
    HTMLTag.removeAttribute("data-sidebar");
    if (skin !== "default") {
      HTMLTag.setAttribute("data-sidebar", skin);
      localStorage.setItem("sidebar-skin", skin);
    } else {
      localStorage.removeItem("sidebar-skin", skin);
    }
  };

  return (
    <div className="header-main px-3 px-lg-4">
      <Link onClick={toggleSidebar} className="menu-link me-3 me-lg-4"><i className="ri-menu-2-fill"></i></Link>

      {/* Command Palette */}
      <div className="form-search me-auto" ref={searchRef} style={{ position: 'relative' }}>
        <input
          type="text"
          className="form-control"
          placeholder="ค้นหาเมนู..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { if (query) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        <i className="ri-search-line"></i>

        {isOpen && results.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'var(--bs-body-bg, #fff)',
            border: '1px solid var(--bs-border-color, #dee2e6)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1050,
            overflow: 'hidden'
          }}>
            {results.map((item, idx) => (
              <div
                key={item.link}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => handleSelect(item)}
                style={{
                  padding: '9px 14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: idx === activeIdx ? 'var(--bs-primary-bg-subtle, #cfe2ff)' : 'transparent',
                  color: 'var(--bs-body-color)',
                  borderBottom: idx < results.length - 1 ? '1px solid var(--bs-border-color-translucent, rgba(0,0,0,.08))' : 'none'
                }}
              >
                <i className={`${item.icon} text-primary`} style={{ fontSize: '1rem', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem' }}>{item.label}</span>
                <i className="ri-corner-down-left-line ms-auto text-muted" style={{ fontSize: '0.75rem', opacity: idx === activeIdx ? 1 : 0 }} />
              </div>
            ))}
          </div>
        )}

        {isOpen && query.trim() && results.length === 0 && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'var(--bs-body-bg, #fff)',
            border: '1px solid var(--bs-border-color, #dee2e6)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1050,
            padding: '12px 14px',
            fontSize: '0.875rem',
            color: 'var(--bs-secondary-color, #6c757d)'
          }}>
            <i className="ri-search-line me-2" />ไม่พบเมนูที่ตรงกับ "{query}"
          </div>
        )}
      </div>

      {isIslamicRoute && (
        <button
          className="btn btn-sm btn-outline-secondary ms-3"
          onClick={() => setLang(lang === 'jawi' ? 'th' : 'jawi')}
          title={lang === 'jawi' ? 'เปลี่ยนเป็นภาษาไทย' : 'تبديل إلى اليوية'}
          style={{ fontWeight: 600, minWidth: 48 }}
        >
          {lang === 'jawi' ? 'TH' : 'ياوي'}
        </button>
      )}

      <Dropdown className="dropdown-skin" align="end">
        <Dropdown.Toggle as={CustomToggle}>
          <i className="ri-settings-3-line"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu className="mt-10-f">
          <label>Skin Mode</label>
          <nav className="nav nav-skin">
            <Link onClick={skinMode} className={localStorage.getItem("skin-mode") ? "nav-link" : "nav-link active"}>Light</Link>
            <Link onClick={skinMode} className={localStorage.getItem("skin-mode") ? "nav-link active" : "nav-link"}>Dark</Link>
          </nav>
          <hr />
          <label>Sidebar Skin</label>
          <nav id="sidebarSkin" className="nav nav-skin">
            <Link onClick={sidebarSkin} className={!(localStorage.getItem("sidebar-skin")) ? "nav-link active" : "nav-link"}>Default</Link>
            <Link onClick={sidebarSkin} className={(localStorage.getItem("sidebar-skin") === "prime") ? "nav-link active" : "nav-link"}>Prime</Link>
            <Link onClick={sidebarSkin} className={(localStorage.getItem("sidebar-skin") === "dark") ? "nav-link active" : "nav-link"}>Dark</Link>
          </nav>
        </Dropdown.Menu>
      </Dropdown>

      <NotificationBell mode="staff" />

      <Dropdown className="dropdown-profile ms-3 ms-xl-4" align="end">
        <Dropdown.Toggle as={CustomToggle}>
          <div className="avatar online">
            <img src={avatarSrc} alt="" />
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu className="mt-10-f">
          <div className="dropdown-menu-body">
            <div className="avatar avatar-xl online mb-3"><img src={avatarSrc} alt="" /></div>
            <h5 className="mb-1 text-dark fw-semibold">{getFullName()}</h5>
            <p className="fs-sm text-secondary">{getPosName()}</p>
            <nav className="nav">
              <Link to="/account/profile/edit"><i className="ri-edit-2-line"></i> แก้ไขโปรไฟล์</Link>
              <Link to="/account/profile"><i className="ri-profile-line"></i> ดูโปรไฟล์</Link>
            </nav>
            <hr />
            <nav className="nav">
              <Link to="/general/help"><i className="ri-question-line"></i> ศูนย์ช่วยเหลือ</Link>
              <Link to="/pages/change-password"><i className="ri-lock-line"></i> ตั้งค่ารหัสผ่าน</Link>
              <Link to="/account/settings"><i className="ri-user-settings-line"></i> ตั้งค่าบัญชีผู้ใช้</Link>
              <Link to="" onClick={() => logout(() => window.location.reload())}><i className="ri-logout-box-r-line"></i> ออกจากระบบ</Link>
            </nav>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}
